import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {MEDIA_KEYS} from "@/api/mediaApi";
import Button from "@/components/Common/Button";
import InputField from "@/components/Common/forms/InputField";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore, {useManagedCampaignType, useManagementCampaign} from "@/store/campaignManagement";
import type {TCarouselMedia} from "@/store/carousel";
import type {TContent} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useToggle} from "react-use";

const VideoCodeForm: FC<{content: TCarouselMedia; className?: string}> = ({content, className}) => {
  const {t} = useTranslation("campaign_active");
  const campaign = useManagementCampaign();
  const campaignType = useManagedCampaignType();
  const tabState = useCampaignManagementStore(state => state.tabState);
  const canHaveVideoCode = useMemo(() => {
    const INSTAGRAM_PRODUCT_TYPES: TContent[] = ["FEED", "POST", "REEL", "REELS"];
    const TIKTOK_PRODUCT_TYPES: TContent[] = ["TIKTOK"];

    return (
      (campaignType === "instagram" &&
        INSTAGRAM_PRODUCT_TYPES.includes(content.instagram_media?.media_product_type!)) ||
      (campaignType === "tiktok" && TIKTOK_PRODUCT_TYPES.includes(content.instagram_media?.media_product_type!))
    );
  }, [campaignType, content.instagram_media?.media_product_type]);
  const isMissingVideoCode = useMemo(
    () =>
      (campaignType === "instagram" &&
        campaign.instagram_branded_content_ads &&
        !content.instagram_media?.video_code) ||
      (campaignType === "tiktok" && campaign.tiktok_spark_ads && !content.instagram_media?.video_code),
    [
      campaignType,
      campaign.instagram_branded_content_ads,
      campaign.tiktok_spark_ads,
      content.instagram_media?.video_code,
    ],
  );
  const mutationKey = useMemo(
    () =>
      campaignType === "tiktok"
        ? MEDIA_KEYS.mTikTokMedia(content.instagram_media?.media_id!)
        : MEDIA_KEYS.mInstagramMedia(content.instagram_media?.id!),
    [campaignType, content.instagram_media?.id, content.instagram_media?.media_id],
  );
  const [showForm, toggleForm] = useToggle(false);
  const [videoCode, setVideoCode] = useState("");

  const {mutate: updateVideoCode, isPending: isUpdatingVideoCode} = useMutation({
    mutationKey,
    mutationFn: () => client.patch(mutationKey[0], {video_code: videoCode}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id!)});
      setVideoCode("");
      toggleForm();
    },
  });

  if (tabState !== "active" || !canHaveVideoCode || !isMissingVideoCode) {
    return null;
  }

  return (
    <Can I="create" a="VideoCodeOperation">
      {showForm ? (
        <div className={clsxm("flex flex-col gap-1", className)}>
          <InputField
            value={videoCode}
            onChange={setVideoCode}
            placeholder={`${t("video_code")}...`}
            className="h-10 text-sm"
          />
          <div className="flex items-center justify-between gap-1">
            <Button
              onClick={() => {
                setVideoCode("");
                toggleForm();
              }}
              buttonStyle="White"
              className="h-auto">
              {t("cancel")}
            </Button>
            <Button
              onClick={() => updateVideoCode()}
              isDisabled={!videoCode || isUpdatingVideoCode}
              buttonStyle="Blue"
              className="h-auto">
              {t("save")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={toggleForm}
          shouldScaleOnHover={false}
          buttonStyle="Danger"
          className={clsxm("text-wrap", className)}>
          {t("add_video_code")}
        </Button>
      )}
    </Can>
  );
};

export default VideoCodeForm;
